import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useSlipPeriods, useSlip, useMyPayslips, type PayslipInstallment } from '../hooks';
import { apiFetch } from '../lib/api-client';
import { Download } from 'lucide-react';
import { useToast } from '../components/ui/toast';

const fmtPlain = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const thaiDateShort = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return `${d} ${THAI_MONTHS_SHORT[(m ?? 1) - 1]} ${(y ?? 0) + 543}`;
};
/** ค่าคอม/OT ที่จ่ายนอกรอบเงินเดือน — โหลดแยกเป็นสลิปรายรอบ (2026-07-30 แยกขาดจากใบงวด) */
function roundKindLabelTh(kind: string): string {
  if (kind === 'commission') return 'ค่าคอม';
  if (kind === 'salary') return 'เงินเดือน';
  return 'จ่ายนอกรอบ';
}

interface PeriodRow { period: string; status: string; sent_at: string | null; }
interface SlipLine { label: string; amount: number; }
interface SlipData {
  period: string; gross: number; net: number;
  base_pay: number; ot_1_pay: number; ot_2_pay: number; ot_3_pay: number;
  additions_total: number;
  tax_amount: number; sso_amount: number; pf_amount: number;
  studentloan_amount: number; companyloan_amount: number;
  unpaid_leave_deduct: number; absent_deduct: number; manual_deductions: number;
  /** หักมาสาย — งวดเก่าไม่มีคอลัมน์นี้ (อ่านสำรองจาก calculation_log.docking.late) */
  late_deduct?: number | null;
  deductions_total: number;
  calculation_log?: { line_items?: { earnings?: SlipLine[]; deductions?: SlipLine[]; earnings_total?: number; deductions_total?: number }; docking?: { late?: number; exit?: number }; ytd?: { gross: number; tax: number; sso: number; pf: number; net: number } } | null;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(n);
}

export function Slip() {
  const [period, setPeriod] = useState<string | null>(null);
  const { data: periods } = useSlipPeriods() as { data: PeriodRow[] | undefined };
  const { data: slip } = useSlip(period) as { data: SlipData | undefined };
  const { data: payslipData } = useMyPayslips();
  const payslips = payslipData?.payslips ?? [];
  const toast = useToast();

  // key เดียวคุมสถานะ "กำลังสร้าง PDF" ของทุกปุ่มในหน้า (สลิปงวด + สลิปรายรอบ)
  // กันดาวน์โหลดซ้อนกันแต่ปุ่มอื่นยังกดได้ปกติ — pattern เดียวกับ MyPayouts.tsx
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  async function openPdf(key: string, path: string) {
    if (openingKey) return;
    setOpeningKey(key);
    try {
      const { pdf_url } = await apiFetch<{ pdf_url: string }>(path);
      window.open(pdf_url, '_blank');
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setOpeningKey(null);
    }
  }
  function openPayslip(p: { period: string; releasable: boolean }) {
    if (!p.releasable) {
      toast.error('สลิปงวดนี้ยังเป็นพรีวิว — โหลดได้เมื่อจ่ายค่าคอมงวดนี้ หรือ HR อนุมัติออกสลิป');
      return;
    }
    void openPdf(`period:${p.period}`, `/me/payslips/${p.period}/slip-pdf`);
  }
  // สลิปรายรอบ (ค่าคอม/OT) — โหลดได้ทันทีที่รอบ paid ไม่ติด gate ของสลิปงวด
  function openRound(i: PayslipInstallment) {
    void openPdf(`round:${i.round_id}`, `/me/payouts/${i.round_id}/slip-pdf`);
  }

  const [pdfLoading, setPdfLoading] = useState(false);
  async function downloadPdf() {
    if (!period || pdfLoading) return;
    setPdfLoading(true);
    try {
      const { pdf_url } = await apiFetch<{ pdf_url: string }>(`/slip/pdf?period=${period}`);
      window.open(pdf_url, '_blank');
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setPdfLoading(false);
    }
  }

  if (!period) {
    return (
      <PageLayout title="สลิปเงินเดือน">
        <div className="space-y-2">
          {payslips.map((p) => {
            // ปุ่มบนสุด = สลิปเงินเดือนล้วน (แยกขาดจากค่าคอม/OT 2026-07-30)
            const paidExtra = p.installments.filter((i) => i.status === 'paid' && i.kind !== 'salary');
            const pendingRounds = p.installments.filter((i) => i.status !== 'paid');
            return (
              <div key={p.period} className="card p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium truncate">เงินเดือนงวด {p.period_label}</p>
                    {/* M3 fix: ยอดตรงนี้ต้องตรงกับตัวเลขในสลิปเงินเดือน PDF ที่กดโหลดด้านขวาเป๊ะ
                        (เดิมบวกค่าคอม/OT ปนด้วย → พนักงานเปิด PDF แล้วเห็นยอดไม่ตรงกับที่จอโชว์) */}
                    {p.has_salary_slip ? (
                      <p className="text-xs text-gray-500">เงินเดือน ฿{fmtPlain(p.salary_net)}</p>
                    ) : (
                      <p className="text-xs text-gray-500">ยอดจ่ายแล้วรวมทั้งงวด ฿{fmtPlain(p.net)}</p>
                    )}
                  </div>
                  {p.has_salary_slip ? (
                    <button
                      onClick={() => openPayslip(p)}
                      disabled={openingKey === `period:${p.period}`}
                      className="shrink-0 inline-flex items-center gap-1 text-[12px] text-brand-600 disabled:opacity-50"
                    >
                      <Download className="w-4 h-4" /> {p.releasable ? 'สลิปเงินเดือน' : 'พรีวิว'}
                    </button>
                  ) : (
                    <span className="shrink-0 text-[11px] text-gray-400 bg-gray-50 rounded-full px-2 py-1">รอเงินเดือนจ่าย</span>
                  )}
                </div>
                {p.has_salary_slip && !p.releasable && (
                  <p className="text-[11px] text-amber-600 bg-amber-50 rounded-full px-2 py-1 inline-block">พรีวิว · รอ HR อนุมัติออกสลิป</p>
                )}
                {(paidExtra.length > 0 || pendingRounds.length > 0) && (
                  <div className="border-t border-gray-100 pt-2 space-y-1.5">
                    {/* M3 fix: โชว์ยอดสุทธิของแต่ละใบตรงนี้ — ยอดต้องตรงกับที่อยู่ในสลิป
                        PDF ของรอบนั้นเป๊ะ (แยกใบ = แยกยอด ไม่มีเลขรวมที่หาที่มาไม่ได้) */}
                    {paidExtra.map((i) => (
                      <button
                        key={i.round_id}
                        onClick={() => openRound(i)}
                        disabled={openingKey === `round:${i.round_id}`}
                        className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-brand-700 disabled:opacity-50"
                      >
                        <span>สลิป{roundKindLabelTh(i.kind)} — จ่าย {thaiDateShort(i.pay_date)} · ฿{fmtPlain(i.net)}</span>
                        <span className="shrink-0 inline-flex items-center gap-1 text-[12px] text-brand-600"><Download className="w-3.5 h-3.5" /> โหลด</span>
                      </button>
                    ))}
                    {pendingRounds.map((i) => (
                      <div key={i.round_id} className="flex items-center justify-between text-sm text-gray-400">
                        <span>{i.title}</span>
                        <span className="text-[11px]">ยังไม่จ่าย</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {(periods ?? []).map((p) => (
            <button key={p.period} onClick={() => setPeriod(p.period)} className="card p-4 w-full text-left hover:bg-gray-50">
              <p className="font-medium">{p.period}</p>
              <p className="text-xs text-gray-500">{p.status === 'sent' ? 'ส่งแล้ว' : 'ปิดรอบแล้ว'}</p>
            </button>
          ))}
          {(periods ?? []).length === 0 && payslips.length === 0 && <p className="text-gray-500">ยังไม่มีสลิป</p>}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`สลิป ${period}`}>
      <button onClick={() => setPeriod(null)} className="text-sm text-brand-600 mb-3">← เลือกรอบอื่น</button>
      {!slip ? <p>กำลังโหลด...</p> : (() => {
        const li = slip.calculation_log?.line_items;
        const lateDeduct = Number(slip.late_deduct ?? slip.calculation_log?.docking?.late ?? 0);
        const exitDeduct = Number(slip.calculation_log?.docking?.exit ?? 0);
        const earnings: SlipLine[] = li?.earnings ?? [
          { label: 'เงินเดือน / ค่าจ้าง', amount: (slip.base_pay ?? 0) + (slip.unpaid_leave_deduct ?? 0) + (slip.absent_deduct ?? 0) + lateDeduct + exitDeduct },
          { label: 'ค่าล่วงเวลา (OT1)', amount: slip.ot_1_pay },
          { label: 'ค่าล่วงเวลา (OT2)', amount: slip.ot_2_pay },
          { label: 'ค่าล่วงเวลา (OT3)', amount: slip.ot_3_pay },
          { label: 'เงินเพิ่ม / เบี้ยเลี้ยง', amount: slip.additions_total },
        ].filter((e) => e.amount !== 0);
        const deductions: SlipLine[] = li?.deductions ?? [
          { label: 'หักลาไม่รับเงิน', amount: slip.unpaid_leave_deduct },
          { label: 'หักขาดงาน', amount: slip.absent_deduct },
          { label: 'หักมาสาย', amount: lateDeduct },
          { label: 'หักวันหลังพ้นสภาพพนักงาน', amount: exitDeduct },
          { label: 'ภาษีหัก ณ ที่จ่าย', amount: slip.tax_amount },
          { label: 'ประกันสังคม', amount: slip.sso_amount },
          { label: 'กองทุนสำรองเลี้ยงชีพ', amount: slip.pf_amount },
          { label: 'กยศ. / กรอ.', amount: slip.studentloan_amount },
          { label: 'เงินกู้บริษัท', amount: slip.companyloan_amount },
          { label: 'รายการหักอื่น ๆ', amount: slip.manual_deductions },
        ].filter((d) => d.amount !== 0);
        // #13 — tie each total to the array actually rendered: only trust the
        // stored *_total when the matching itemized array is present (it includes
        // §76 carry/credit lines that the scalar fallback array never has).
        const earnTotal = li?.earnings ? (li.earnings_total ?? earnings.reduce((t, e) => t + e.amount, 0)) : earnings.reduce((t, e) => t + e.amount, 0);
        const dedTotal = li?.deductions ? (li.deductions_total ?? deductions.reduce((t, e) => t + e.amount, 0)) : deductions.reduce((t, e) => t + e.amount, 0);
        return (
        <div className="card p-4 space-y-3">
          <div>
            <p className="text-sm font-medium mb-1 text-green-700">รายได้</p>
            <div className="text-sm space-y-1 ml-2 text-gray-700">
              {earnings.map((e, i) => <div key={i} className="flex justify-between"><span>{e.label}</span><span>{fmt(e.amount)}</span></div>)}
              {earnings.length === 0 && <div className="text-gray-400">— ไม่มี —</div>}
            </div>
            <div className="flex justify-between border-t mt-1 pt-1 font-semibold"><span>รวมรายได้</span><span>{fmt(earnTotal)}</span></div>
          </div>
          <div>
            <p className="text-sm font-medium mb-1 text-red-600">รายการหัก</p>
            <div className="text-sm space-y-1 ml-2 text-gray-700">
              {deductions.map((d, i) => <div key={i} className="flex justify-between"><span>{d.label}</span><span>{d.amount < 0 ? `+${fmt(-d.amount)}` : `-${fmt(d.amount)}`}</span></div>)}
              {deductions.length === 0 && <div className="text-gray-400">— ไม่มี —</div>}
            </div>
            <div className="flex justify-between border-t mt-1 pt-1 font-semibold"><span>รวมรายการหัก</span><span>-{fmt(dedTotal)}</span></div>
          </div>
          <div className="flex justify-between border-t pt-2 text-lg font-bold text-brand-700">
            <span>เงินสุทธิ</span><span>{fmt(slip.net)}</span>
          </div>
          {slip.calculation_log?.ytd && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-500">ยอดสะสมตั้งแต่ต้นปี (YTD)</p>
              <div className="flex justify-between"><span>รายได้สะสม</span><span>{fmt(slip.calculation_log.ytd.gross)}</span></div>
              <div className="flex justify-between"><span>ภาษีสะสม</span><span>{fmt(slip.calculation_log.ytd.tax)}</span></div>
              <div className="flex justify-between"><span>ประกันสังคมสะสม</span><span>{fmt(slip.calculation_log.ytd.sso)}</span></div>
              <div className="flex justify-between"><span>กองทุนสำรองสะสม</span><span>{fmt(slip.calculation_log.ytd.pf)}</span></div>
              <div className="flex justify-between font-medium text-gray-700"><span>สุทธิสะสม</span><span>{fmt(slip.calculation_log.ytd.net)}</span></div>
            </div>
          )}
          {/* Conservative-withholding notice — ระบบหักภาษีเฉพาะลดหย่อนพื้นฐาน
              (60k + ค่าใช้จ่าย + SSO + PF). ผู้มีลดหย่อนเพิ่มต้องขอคืนตอนยื่น ภงด.91 */}
          {slip.tax_amount > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p className="font-medium mb-0.5">ทำไมหักภาษีเท่านี้?</p>
              <p className="leading-relaxed">
                ระบบหักภาษีโดยคำนวณจากลดหย่อนพื้นฐาน
                (ส่วนตัว 60,000 + ค่าใช้จ่าย + ปกส. + PF) เท่านั้น —
                <strong> ยังไม่รวม</strong> คู่สมรส/บุตร/ประกัน/RMF/SSF/ดอกเบี้ยบ้าน.
                หากมีรายการเหล่านี้ คุณสามารถ <strong>ขอคืนตอนยื่น ภงด.91</strong> ปลายปีได้
              </p>
              <Link to="/factsheet#tax" className="inline-block mt-1 underline text-amber-700 hover:text-amber-900">
                ดูรายละเอียดวิธีคิดภาษี →
              </Link>
            </div>
          )}
          <button onClick={downloadPdf} disabled={pdfLoading} className="btn-cta w-full">
            <Download className="w-4 h-4" /> {pdfLoading ? 'กำลังสร้าง PDF…' : 'ดาวน์โหลด PDF'}
          </button>
          <div className="text-xs text-center text-gray-500 space-x-3">
            <Link to="/factsheet#tax" className="hover:text-brand-700 underline">ภาษี</Link>
            <Link to="/factsheet#sso" className="hover:text-brand-700 underline">ประกันสังคม</Link>
            <Link to="/factsheet#pf" className="hover:text-brand-700 underline">กองทุน</Link>
            <Link to="/factsheet#ytd" className="hover:text-brand-700 underline">YTD</Link>
          </div>
        </div>
        );
      })()}
    </PageLayout>
  );
}
